import { Layout } from "../../UI/Layout/Layout.tsx";
import { NavBar } from "../../UI/Layout/NavBar.tsx";
import { Footer } from "../../UI/Layout/Footer.tsx";
import { FullPageLoading } from "../../UI/FullPageLoading.tsx";
import { PrimaryButton, TextInput } from "@normasteaching/norma-ui";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router";
import { useRef, useState, useEffect, useMemo } from "react";
import { isItemDisabled } from "../../ProductService/isItemDisabled.ts";
import { Switch } from "../../UI/Switch.tsx";
import { buildListCountLabel } from "../buildListCountLabel.ts";
import { AdminMainContainer } from "../../UI/Admin/AdminMainContainer.tsx";
import { SalesStatisticsButton } from "../SalesStatistics/SalesStatisticsButton.tsx";
import { AdminNormasTeachingBundleTable } from "./AdminNormasTeachingBundleTable.tsx";
import { CreateNormasTeachingBundleScreenPath } from "./CreateNormasTeachingBundleScreen.tsx";
import { useAllNormasTeachingBundles } from "../../ProductService/NormasTeachingBundle/useAllNormasTeachingBundles.ts";
import { ErrorState } from "../../UI/ErrorState.tsx";

export const AdminAllNormasTeachingBundleScreenPath =
  "/admin/normasteaching/all-bundles";
export function AdminAllNormasTeachingBundleScreen() {
  const [searchInputValue, setSearchInputValue] = useState("");
  const [search, setSearch] = useState("");
  const [showDisabled, setShowDisabled] = useState(false);
  const refetchTimeoutRef = useRef<NodeJS.Timeout>();

  const { bundles, isLoading, error } = useAllNormasTeachingBundles({
    search,
  });
  const navigate = useNavigate();

  // Filtered here rather than server-side: `/bundles` is shared with the
  // coupon, promo and order screens, which must keep seeing every bundle.
  const visibleBundles = useMemo(
    () => (showDisabled ? bundles : bundles.filter((b) => !isItemDisabled(b))),
    [bundles, showDisabled],
  );

  const disabledCount = useMemo(
    () => bundles.filter(isItemDisabled).length,
    [bundles],
  );

  useEffect(() => {
    return () => clearTimeout(refetchTimeoutRef.current);
  }, []);

  if (isLoading) return <FullPageLoading />;
  return (
    <Layout>
      <AdminMainContainer>
        <NavBar showAdminSideBarHandle />
        <div className={"flex-1 p-4 flex flex-col gap-4"}>
          <div
            className={
              "flex items-center justify-between gap-2 md:flex-row flex-col"
            }
          >
            <h1 className={"font-bold text-3xl flex-1"}>
              Bundle Norma's Teaching
            </h1>
            <div className={"flex items-center gap-2"}>
              <SalesStatisticsButton />
              <PrimaryButton
                iconRight={<Plus className={"w-5"} />}
                label={"Nuovo bundle di prodotti"}
                onClick={() => navigate(CreateNormasTeachingBundleScreenPath)}
              />
            </div>
          </div>
          <TextInput
            placeholder="Cerca per nome, tag AC, analytics tag, ID prodotto, ID bundle, prezzo..."
            value={searchInputValue}
            onChange={(e) => {
              const value = e.target.value;
              setSearchInputValue(value);
              clearTimeout(refetchTimeoutRef.current);
              refetchTimeoutRef.current = setTimeout(() => {
                setSearch(value);
              }, 500);
            }}
          />
          {error ? (
            <ErrorState />
          ) : (
            <div
              className={"flex items-center justify-between gap-4 flex-wrap"}
            >
              <p className={"text-sm text-gray-500"}>
                {buildListCountLabel({
                  visible: visibleBundles.length,
                  disabled: disabledCount,
                  showDisabled,
                  noun: ["bundle", "bundle"],
                })}
              </p>
              <Switch
                label={"Mostra disattivati"}
                checked={showDisabled}
                onChange={setShowDisabled}
              />
            </div>
          )}
          <div className={"overflow-x-auto"}>
            <AdminNormasTeachingBundleTable bundles={visibleBundles} />
          </div>
        </div>
      </AdminMainContainer>

      <Footer />
    </Layout>
  );
}
